import React, {
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import { useHistory, useLocation } from "react-router-dom";
import dayjs from "dayjs";

import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import SendOutlinedIcon from "@mui/icons-material/SendOutlined";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import Container from "@mui/material/Container";
import LinearProgress from "@mui/material/LinearProgress";
import Snackbar from "@mui/material/Snackbar";
import Typography from "@mui/material/Typography";

import { DatabaseContext, UserContext } from "../../context";
import firebase from "../../firebase";
import { makeid, surveyDisplayNumbers } from "../../utility";
import ToolbarWithLogo from "../ToolbarWithLogo";
import SurveyQuestion from "./SurveyQuestion";

const surfaceSx = {
    borderRadius: 3,
    borderColor: "divider",
    boxShadow: "0 7px 24px rgba(20, 35, 25, 0.055)",
};

function mapSurveySnapshot(snapshot) {
    const data = snapshot.val();
    const questions = Object.entries(data?.questions || {})
        .map(([key, question]) => ({
            key,
            row: question.row,
            questionText: question.questionText,
            answerFormat: question.answerFormat,
            answerRequired:
                question.answerRequired === true ||
                question.answerRequired === "true",
            fileType: question.fileType,
            linkKey: question.linkKey,
            multipleChoiceOptions: Object.entries(
                question.multipleChoiceOptions || {},
            )
                .map(([optionKey, option]) => ({
                    key: optionKey,
                    index: option.index,
                    text: option.text,
                }))
                .sort((first, second) => first.index - second.index),
        }))
        .sort((first, second) => first.row - second.row);

    return {
        key: snapshot.key,
        questions,
    };
}

export default function SurveyResponsePage() {
    const { currentUserData } = useContext(UserContext);
    const {
        surveyInfo = [],
        surveyInfoLoaded = false,
    } = useContext(DatabaseContext);
    const location = useLocation();
    const history = useHistory();

    const [survey, setSurvey] = useState();
    const [isSurveyLoading, setIsSurveyLoading] = useState(true);
    const [loadError, setLoadError] = useState("");
    const [showRequiredText, setShowRequiredText] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [statusIsVisible, setStatusIsVisible] = useState(false);
    const [statusMessage, setStatusMessage] = useState("");
    const hasSubmittedRef = useRef(false);

    const surveyKey = useMemo(
        () => new URLSearchParams(location.search).get("key"),
        [location.search],
    );
    const surveyDetails = useMemo(
        () => surveyInfo.find((item) => item.key === surveyKey),
        [surveyInfo, surveyKey],
    );

    const loadSurvey = useCallback(async (key) => {
        const snapshot = await firebase
            .database()
            .ref("surveys")
            .child(key)
            .once("value");

        if (!snapshot.exists() && !snapshot.val()) {
            throw new Error("Survey not found");
        }

        return mapSurveySnapshot(snapshot);
    }, []);

    useEffect(() => {
        let isCurrent = true;

        if (!currentUserData || !surveyInfoLoaded) {
            setIsSurveyLoading(true);
            return () => {
                isCurrent = false;
            };
        }

        if (!surveyKey || !surveyDetails) {
            setSurvey(undefined);
            setLoadError(
                "This survey is unavailable or you no longer have access to it.",
            );
            setIsSurveyLoading(false);
            return () => {
                isCurrent = false;
            };
        }

        setSurvey(undefined);
        setLoadError("");
        setIsSurveyLoading(true);
        setShowRequiredText(false);
        setIsSubmitted(false);
        hasSubmittedRef.current = false;

        loadSurvey(surveyKey)
            .then((loadedSurvey) => {
                if (isCurrent) {
                    setSurvey(loadedSurvey);
                    setIsSurveyLoading(false);
                }
            })
            .catch((error) => {
                console.error("Error loading survey:", error);
                if (isCurrent) {
                    setLoadError(
                        "The survey could not be loaded. Please check your connection and try again.",
                    );
                    setIsSurveyLoading(false);
                }
            });

        return () => {
            isCurrent = false;
        };
    }, [
        currentUserData,
        loadSurvey,
        surveyDetails,
        surveyInfoLoaded,
        surveyKey,
    ]);

    function questionHasBeenAnswered(question) {
        if (question.answerFormat === "File") {
            return true;
        }
        if (
            question.answerFormat === "Short Text" ||
            question.answerFormat === "Long Text" ||
            question.answerFormat === "True or False" ||
            question.answerFormat === "Multiple Choice"
        ) {
            return Boolean(question.responseTextAnswer);
        }
        if (question.answerFormat === "Number") {
            return (
                question.responseNumberAnswer !== undefined &&
                question.responseNumberAnswer !== ""
            );
        }
        if (question.answerFormat === "Checkboxes") {
            return Boolean(question.responseCheckboxAnswers?.length);
        }
        if (question.answerFormat === "Date") {
            return Boolean(question.responseDateAnswer);
        }

        return false;
    }

    const requiredQuestions = useMemo(
        () =>
            (survey?.questions || []).filter(
                (question) =>
                    question.answerRequired &&
                    question.answerFormat !== "File",
            ),
        [survey],
    );
    const questionDisplayNumbers = surveyDisplayNumbers(survey?.questions);
    const answeredRequiredCount = requiredQuestions.filter(
        questionHasBeenAnswered,
    ).length;
    const requiredProgress =
        requiredQuestions.length === 0
            ? 100
            : (answeredRequiredCount / requiredQuestions.length) * 100;

    function handleAnswerChange(questionKey, field, value) {
        setSurvey((current) => ({
            ...current,
            questions: current.questions.map((question) =>
                question.key === questionKey
                    ? { ...question, [field]: value }
                    : question,
            ),
        }));
    }

    function canSubmit() {
        if (!survey?.questions?.length) {
            return false;
        }

        return requiredQuestions.every(questionHasBeenAnswered);
    }

    function buildAnswer(question) {
        const answer = {
            question: question.key,
            questionText: question.questionText,
            answerFormat: question.answerFormat,
        };

        if (
            question.answerFormat === "Short Text" ||
            question.answerFormat === "Long Text" ||
            question.answerFormat === "True or False" ||
            question.answerFormat === "Multiple Choice"
        ) {
            if (question.responseTextAnswer !== undefined) {
                answer.answer = question.responseTextAnswer;
            }
        } else if (question.answerFormat === "Number") {
            if (
                question.responseNumberAnswer !== undefined &&
                question.responseNumberAnswer !== ""
            ) {
                answer.answer = String(question.responseNumberAnswer);
            }
        } else if (question.answerFormat === "Checkboxes") {
            if (question.responseCheckboxAnswers?.length) {
                answer.answer =
                    question.responseCheckboxAnswers.join("£$%");
            }
        } else if (question.answerFormat === "Date") {
            if (question.responseDateAnswer) {
                answer.answer = question.responseDateAnswer.toString();
            }
        }

        return answer.answer === undefined ? null : answer;
    }

    async function handleSubmit() {
        if (isSubmitting || hasSubmittedRef.current) {
            return;
        }

        if (!canSubmit()) {
            setShowRequiredText(true);
            setStatusMessage(
                "Please answer all required questions before submitting.",
            );
            setStatusIsVisible(true);
            return;
        }

        try {
            const connectedSnapshot = await firebase
                .database()
                .ref(".info/connected")
                .once("value");

            if (!connectedSnapshot.val()) {
                setStatusMessage(
                    "You appear to be offline. Check your connection and try again.",
                );
                setStatusIsVisible(true);
                return;
            }
        } catch (error) {
            console.error("Connection check failed:", error);
        }

        setIsSubmitting(true);
        hasSubmittedRef.current = true;

        const surveySnapshot = JSON.parse(JSON.stringify(survey));
        const responseKey = makeid(19);
        const responseRef = firebase
            .database()
            .ref("surveyresponses/")
            .child(responseKey);
        let timeoutId;

        try {
            const submissionPromise = (async () => {
                await responseRef.set({
                    survey: surveySnapshot.key,
                    surveyTitle:
                        surveyDetails.title || "Untitled survey",
                    submissionDate: dayjs().toString(),
                    user:
                        surveyDetails.anonymous === false
                            ? currentUserData.key
                            : "Anonymous",
                });

                const answers = surveySnapshot.questions
                    .map(buildAnswer)
                    .filter(Boolean);
                await Promise.all(
                    answers.map((answer) =>
                        responseRef.child("answers").push(answer),
                    ),
                );
            })();
            const timeoutPromise = new Promise((_, reject) => {
                timeoutId = setTimeout(
                    () => reject(new Error("Submission timeout")),
                    30000,
                );
            });

            await Promise.race([submissionPromise, timeoutPromise]);
            clearTimeout(timeoutId);
            setIsSubmitting(false);
            setStatusIsVisible(false);
            setIsSubmitted(true);
        } catch (error) {
            clearTimeout(timeoutId);
            console.error("Submission error:", error);
            setIsSubmitting(false);
            hasSubmittedRef.current = false;
            setStatusMessage(
                error.message === "Submission timeout"
                    ? "Submission is taking too long. Check your connection and try again."
                    : "The survey could not be submitted. Please try again.",
            );
            setStatusIsVisible(true);
        }
    }

    const isLoading =
        !currentUserData || !surveyInfoLoaded || isSurveyLoading;

    return (
        <>
            <Box
                sx={{
                    minHeight: {
                        xs: "calc(100dvh - 60px)",
                        sm: "calc(100vh - 128px)",
                    },
                    pb: 12,
                    backgroundColor: "background.default",
                }}
            >
                <Container
                    maxWidth="md"
                    sx={{ px: { xs: 1.5, sm: 3 }, py: { xs: 2, sm: 3 } }}
                >
                    {!isSubmitted && (
                        <Button
                            color="inherit"
                            startIcon={<ArrowBackIcon />}
                            onClick={() => history.goBack()}
                            sx={{ mb: 2 }}
                        >
                            Back to surveys
                        </Button>
                    )}

                    {isLoading ? (
                        <Card variant="outlined" sx={surfaceSx}>
                            <CardContent
                                sx={{
                                    minHeight: 360,
                                    display: "flex",
                                    flexDirection: "column",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    color: "text.secondary",
                                }}
                            >
                                <CircularProgress
                                    size={34}
                                    aria-label="Loading survey"
                                />
                                <Typography variant="body2" sx={{ mt: 1.5 }}>
                                    Loading survey…
                                </Typography>
                            </CardContent>
                        </Card>
                    ) : loadError ? (
                        <Card variant="outlined" sx={surfaceSx}>
                            <CardContent
                                sx={{
                                    minHeight: 300,
                                    display: "flex",
                                    flexDirection: "column",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    px: 3,
                                    textAlign: "center",
                                }}
                            >
                                <ErrorOutlineIcon
                                    color="error"
                                    sx={{ fontSize: 48, mb: 1.25 }}
                                />
                                <Typography variant="h6">
                                    Survey unavailable
                                </Typography>
                                <Typography
                                    variant="body2"
                                    color="text.secondary"
                                    sx={{ mt: 0.75, maxWidth: 480 }}
                                >
                                    {loadError}
                                </Typography>
                            </CardContent>
                        </Card>
                    ) : isSubmitted ? (
                        <Card variant="outlined" sx={surfaceSx}>
                            <CardContent
                                sx={{
                                    minHeight: 420,
                                    display: "flex",
                                    flexDirection: "column",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    px: { xs: 2.5, sm: 5 },
                                    py: 5,
                                    textAlign: "center",
                                    "&:last-child": { pb: 5 },
                                }}
                            >
                                <Box
                                    sx={{
                                        width: 72,
                                        height: 72,
                                        display: "grid",
                                        placeItems: "center",
                                        mb: 2,
                                        borderRadius: "50%",
                                        color: "primary.main",
                                        backgroundColor:
                                            "rgba(0, 149, 61, 0.1)",
                                    }}
                                >
                                    <CheckCircleOutlineIcon
                                        sx={{ fontSize: 42 }}
                                    />
                                </Box>
                                <Typography
                                    variant="h4"
                                    component="h1"
                                    sx={{ fontSize: { xs: 28, sm: 34 } }}
                                >
                                    Response submitted
                                </Typography>
                                <Typography
                                    variant="body1"
                                    color="text.secondary"
                                    sx={{ mt: 1, maxWidth: 520 }}
                                >
                                    Thank you. Your response to{" "}
                                    <Box
                                        component="span"
                                        sx={{
                                            color: "text.primary",
                                            fontWeight: 800,
                                        }}
                                    >
                                        {surveyDetails.title ||
                                            "this survey"}
                                    </Box>{" "}
                                    has been saved successfully.
                                </Typography>
                                <Button
                                    variant="contained"
                                    disableElevation
                                    size="large"
                                    onClick={() =>
                                        history.push("/surveys/s")
                                    }
                                    sx={{ mt: 3 }}
                                >
                                    Back to surveys
                                </Button>
                            </CardContent>
                        </Card>
                    ) : (
                        <Box
                            component="form"
                            onSubmit={(event) => {
                                event.preventDefault();
                                handleSubmit();
                            }}
                            sx={{ display: "grid", gap: 2 }}
                        >
                            <Card variant="outlined" sx={surfaceSx}>
                                <CardContent
                                    sx={{
                                        p: { xs: 2.5, sm: 3 },
                                        "&:last-child": {
                                            pb: { xs: 2.5, sm: 3 },
                                        },
                                    }}
                                >
                                    <Box
                                        sx={{
                                            display: "flex",
                                            alignItems: "flex-start",
                                            gap: 1.5,
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                width: 46,
                                                height: 46,
                                                display: "grid",
                                                placeItems: "center",
                                                flexShrink: 0,
                                                borderRadius: 2,
                                                color: "primary.main",
                                                backgroundColor:
                                                    "rgba(0, 149, 61, 0.09)",
                                            }}
                                        >
                                            <AssignmentOutlinedIcon />
                                        </Box>
                                        <Box sx={{ flex: 1, minWidth: 0 }}>
                                            <Typography
                                                variant="h4"
                                                component="h1"
                                                sx={{
                                                    fontSize: {
                                                        xs: 28,
                                                        sm: 34,
                                                    },
                                                    lineHeight: 1.15,
                                                    overflowWrap: "anywhere",
                                                }}
                                            >
                                                {surveyDetails.title ||
                                                    "Untitled survey"}
                                            </Typography>
                                            {surveyDetails.intro && (
                                                <Typography
                                                    variant="body1"
                                                    color="text.secondary"
                                                    sx={{
                                                        mt: 1,
                                                        whiteSpace: "pre-wrap",
                                                    }}
                                                >
                                                    {surveyDetails.intro}
                                                </Typography>
                                            )}
                                        </Box>
                                    </Box>

                                    <Box
                                        sx={{
                                            display: "flex",
                                            flexWrap: "wrap",
                                            gap: 1,
                                            mt: 2.5,
                                        }}
                                    >
                                        <Chip
                                            icon={<LockOutlinedIcon />}
                                            label={
                                                surveyDetails.anonymous ===
                                                false
                                                    ? "Your name is recorded"
                                                    : "Anonymous response"
                                            }
                                            sx={{
                                                backgroundColor:
                                                    "rgba(20, 35, 25, 0.06)",
                                            }}
                                        />
                                        <Chip
                                            label={`${survey.questions.length} ${
                                                survey.questions.length === 1
                                                    ? "item"
                                                    : "items"
                                            }`}
                                            sx={{
                                                backgroundColor:
                                                    "rgba(20, 35, 25, 0.06)",
                                            }}
                                        />
                                    </Box>

                                    {requiredQuestions.length > 0 && (
                                        <Box sx={{ mt: 2.5 }}>
                                            <Box
                                                sx={{
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent:
                                                        "space-between",
                                                    gap: 2,
                                                    mb: 0.75,
                                                }}
                                            >
                                                <Typography
                                                    variant="body2"
                                                    sx={{ fontWeight: 800 }}
                                                >
                                                    Required questions
                                                </Typography>
                                                <Typography
                                                    variant="body2"
                                                    color="text.secondary"
                                                >
                                                    {answeredRequiredCount} of{" "}
                                                    {requiredQuestions.length}
                                                </Typography>
                                            </Box>
                                            <LinearProgress
                                                variant="determinate"
                                                value={requiredProgress}
                                                sx={{
                                                    height: 7,
                                                    borderRadius: 99,
                                                }}
                                            />
                                        </Box>
                                    )}
                                </CardContent>
                            </Card>

                            {survey.questions.length === 0 ? (
                                <Card variant="outlined" sx={surfaceSx}>
                                    <CardContent
                                        sx={{
                                            minHeight: 220,
                                            display: "flex",
                                            flexDirection: "column",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            textAlign: "center",
                                        }}
                                    >
                                        <CheckCircleOutlineIcon
                                            sx={{
                                                fontSize: 44,
                                                color: "text.disabled",
                                                mb: 1,
                                            }}
                                        />
                                        <Typography variant="h6">
                                            No questions yet
                                        </Typography>
                                        <Typography
                                            variant="body2"
                                            color="text.secondary"
                                            sx={{ mt: 0.5 }}
                                        >
                                            This survey does not currently have
                                            anything to complete.
                                        </Typography>
                                    </CardContent>
                                </Card>
                            ) : (
                                survey.questions.map((question, index) => (
                                    <SurveyQuestion
                                        key={question.key}
                                        displayNumber={
                                            questionDisplayNumbers[index]
                                        }
                                        surveyKey={survey.key}
                                        question={question}
                                        showRequiredText={showRequiredText}
                                        questionHasBeenAnswered={
                                            questionHasBeenAnswered
                                        }
                                        onAnswerChange={handleAnswerChange}
                                    />
                                ))
                            )}

                            <Card variant="outlined" sx={surfaceSx}>
                                <CardContent
                                    sx={{
                                        p: { xs: 2, sm: 2.5 },
                                        display: "flex",
                                        alignItems: {
                                            xs: "stretch",
                                            sm: "center",
                                        },
                                        flexDirection: {
                                            xs: "column",
                                            sm: "row",
                                        },
                                        gap: 2,
                                        "&:last-child": {
                                            pb: { xs: 2, sm: 2.5 },
                                        },
                                    }}
                                >
                                    <Box sx={{ flex: 1 }}>
                                        <Typography
                                            variant="subtitle1"
                                            sx={{ fontWeight: 800 }}
                                        >
                                            Ready to submit?
                                        </Typography>
                                        <Typography
                                            variant="body2"
                                            color="text.secondary"
                                        >
                                            Check your answers before sending.
                                            You cannot edit this response
                                            afterwards.
                                        </Typography>
                                    </Box>
                                    <Button
                                        type="submit"
                                        variant="contained"
                                        disableElevation
                                        size="large"
                                        startIcon={
                                            isSubmitting ? (
                                                <CircularProgress
                                                    size={18}
                                                    color="inherit"
                                                />
                                            ) : (
                                                <SendOutlinedIcon />
                                            )
                                        }
                                        disabled={
                                            isSubmitting ||
                                            survey.questions.length === 0
                                        }
                                    >
                                        {isSubmitting
                                            ? "Submitting…"
                                            : "Submit response"}
                                    </Button>
                                </CardContent>
                                {isSubmitting && <LinearProgress />}
                            </Card>
                        </Box>
                    )}
                </Container>
            </Box>

            <Snackbar
                open={statusIsVisible}
                autoHideDuration={6000}
                onClose={(event, reason) => {
                    if (reason !== "clickaway") {
                        setStatusIsVisible(false);
                    }
                }}
            >
                <Alert
                    onClose={() => setStatusIsVisible(false)}
                    severity="error"
                    variant="filled"
                >
                    {statusMessage}
                </Alert>
            </Snackbar>
            <ToolbarWithLogo />
        </>
    );
}
